/**
 * Normalise a vendor website address so it is safe to send and to render as a link.
 *
 * Mirrors jobviousapp/services/websiteUrl.js in JobviousServer — keep both in
 * sync. The server re-validates; this copy exists so the user gets the error
 * inline instead of a failed save.
 *
 * `new URL()` alone is not a sufficient check: it accepts `javascript:` and
 * `data:` URLs, which become script injection once rendered into an href.
 *
 *   normalizeWebsite('diydetail.com')          -> 'https://diydetail.com'
 *   normalizeWebsite('https://diydetail.com/') -> 'https://diydetail.com'
 *   normalizeWebsite('')                       -> null   (clears the value)
 *   normalizeWebsite('javascript:alert(1)')    -> throws
 */
export const WEBSITE_MAX_LENGTH = 500;

export function normalizeWebsite(value) {
    if (value === undefined || value === null) return null;
    let raw = String(value).trim();
    if (raw === '') return null;

    if (!/^[a-z][a-z0-9+.-]*:/i.test(raw)) raw = `https://${raw}`;

    let url;
    try {
        url = new URL(raw);
    } catch (e) {
        throw new Error('Enter a valid web address, e.g. example.com');
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new Error('Website must start with http:// or https://');
    }
    if (!url.hostname.includes('.')) {
        throw new Error('Website must include a domain, e.g. example.com');
    }

    const out = (url.pathname === '/' && !url.search && !url.hash) ? url.origin : url.toString();
    if (out.length > WEBSITE_MAX_LENGTH) throw new Error(`Website must be ${WEBSITE_MAX_LENGTH} characters or fewer`);
    return out;
}

/** A link-safe href for display, or null when the stored value is not a safe web address. */
export function safeWebsiteHref(value) {
    try { return normalizeWebsite(value); } catch (e) { return null; }
}
